import { useRef } from 'react'
import { CopyPlusIcon, PencilIcon, Trash2Icon } from 'lucide-react'
import { formatDecimalHours } from '@coneno/c-sheet-shared'
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuGroup,
  ContextMenuItem
} from '@/components/ui/context-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

type EntryChipProps = {
  startTime: string
  endTime: string
  durationMinutes: number
  description: string | null
  /** Shown when the entry needs attention, such as overlapping another entry. */
  warning?: string
  ariaLabel: string
  onKeyDown(event: React.KeyboardEvent<HTMLButtonElement>): void
  onEdit(anchor: HTMLElement): void
  onClone(): void
  onDelete(): void
  deleting: boolean
}

/** One entry in the Project sheet grid: its hours above its time range, opening the editor on click. */
export function EntryChip({
  startTime,
  endTime,
  durationMinutes,
  description,
  warning,
  ariaLabel,
  onKeyDown,
  onEdit,
  onClone,
  onDelete,
  deleting
}: EntryChipProps): React.JSX.Element {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const chip = (
    <button
      ref={buttonRef}
      type="button"
      className={cn(
        'flex w-full flex-col items-start rounded-md border bg-card px-1.5 py-1 text-left tabular-nums shadow-xs transition-colors outline-none hover:border-foreground/30 focus-visible:ring-3 focus-visible:ring-ring/50',
        warning && 'border-amber-500 bg-amber-50 hover:border-amber-600 dark:bg-amber-950'
      )}
      aria-label={warning ? `${ariaLabel}. ${warning}` : ariaLabel}
      data-grid-nav=""
      onKeyDown={onKeyDown}
      onClick={(event) => onEdit(event.currentTarget)}
    >
      <span className="text-sm font-semibold">{formatDecimalHours(durationMinutes)} h</span>
      <span className="text-[11px] tracking-tight whitespace-nowrap text-muted-foreground">
        {startTime}–{endTime}
      </span>
    </button>
  )

  return (
    <ContextMenu>
      {description || warning ? (
        <Tooltip>
          <TooltipTrigger render={<ContextMenuTrigger render={chip} />} />
          <TooltipContent
            side="right"
            className="flex max-w-64 flex-col gap-1 text-left whitespace-normal"
          >
            {warning && <span className="font-medium">{warning}</span>}
            {description && <span>{description}</span>}
          </TooltipContent>
        </Tooltip>
      ) : (
        <ContextMenuTrigger render={chip} />
      )}
      <ContextMenuContent finalFocus={false}>
        <ContextMenuGroup>
          <ContextMenuItem
            onClick={() => {
              if (buttonRef.current) onEdit(buttonRef.current)
            }}
          >
            <PencilIcon />
            Edit
          </ContextMenuItem>
          <ContextMenuItem onClick={onClone}>
            <CopyPlusIcon />
            Clone entry to…
          </ContextMenuItem>
          <ContextMenuItem variant="destructive" disabled={deleting} onClick={onDelete}>
            <Trash2Icon />
            Delete entry
          </ContextMenuItem>
        </ContextMenuGroup>
      </ContextMenuContent>
    </ContextMenu>
  )
}
